import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { loadGsap, prefersReducedMotion } from '../lib/gsap';

// About keeps native scrolling — CSS sticky on the story image breaks under ScrollSmoother transforms.
// Auth/legal pages are short — native scroll avoids ScrollSmoother height quirks.
const NATIVE_SCROLL_ROUTES = new Set([
  '/about',
  '/log-in',
  '/login',
  '/registration',
  '/register',
]);

const isTouchDevice = () =>
  typeof window !== 'undefined'
  && window.matchMedia('(hover: none) and (pointer: coarse)').matches;

function SmoothScroll({ children }) {
  const location = useLocation();
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const smootherRef = useRef(null);

  useEffect(() => {
    if (NATIVE_SCROLL_ROUTES.has(location.pathname) || prefersReducedMotion()) {
      return undefined;
    }

    let cancelled = false;
    let smoother;
    let scrollTrigger;
    let refreshTimer;

    const requestRefresh = () => {
      if (cancelled || !scrollTrigger) return;
      scrollTrigger.refresh();
    };

    (async () => {
      const { ScrollSmoother, ScrollTrigger } = await loadGsap();
      if (cancelled || !wrapperRef.current || !contentRef.current) return;

      scrollTrigger = ScrollTrigger;
      ScrollSmoother.get()?.kill();

      smoother = ScrollSmoother.create({
        wrapper: wrapperRef.current,
        content: contentRef.current,
        // Lower catch-up reads as responsive rather than floaty/laggy.
        smooth: 0.8,
        // Emulated momentum on touch always feels worse than the native one.
        smoothTouch: false,
        effects: true,
        // Only needed on touch (address-bar resize); on desktop it hijacks the
        // wheel and makes scrolling feel heavy.
        normalizeScroll: isTouchDevice(),
        ignoreMobileResize: true,
      });
      smootherRef.current = smoother;

      smoother.scrollTo(0, false);
      document.documentElement.classList.add('has-gsap-smooth-scroll');

      // Late-loading images/fonts change page height; without a refresh the
      // smoother scrolls against a stale height and stutters near the bottom.
      requestRefresh();
      refreshTimer = window.setTimeout(requestRefresh, 600);
      window.addEventListener('load', requestRefresh);
      document.fonts?.ready.then(requestRefresh).catch(() => {});
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(refreshTimer);
      window.removeEventListener('load', requestRefresh);
      smoother?.kill();
      smootherRef.current = null;
      document.documentElement.classList.remove('has-gsap-smooth-scroll');
    };
  }, [location.pathname]);

  const handleClick = (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const hash = link.getAttribute('href');
    if (!hash || hash === '#') return;
    const target = document.querySelector(hash);
    const smoother = smootherRef.current;
    if (!target || !smoother) return;

    event.preventDefault();
    smoother.scrollTo(target, true, 'top top');
    window.history.replaceState(null, '', hash);
  };

  return (
    <div id="smooth-wrapper" ref={wrapperRef}>
      <div id="smooth-content" ref={contentRef} onClick={handleClick}>
        {children}
      </div>
    </div>
  );
}

export default SmoothScroll;
